import type {
  GiftCard,
  PatchAdminV1StoresStoreIdGiftCardsIdBody,
  PostAdminV1StoresStoreIdGiftCardsBody,
  PostAdminV1StoresStoreIdGiftCardsIdAdjustBody,
  PostAdminV1StoresStoreIdGiftCardsIdSendBody,
} from '@nucleus-commerce/api-client/admin';
import { invariant } from '@nucleus-commerce/core';

import type {
  AdjustGiftCardInput,
  AdjustGiftCardOutput,
  GiftCardEditInput,
  GiftCardEditOutput,
  IssueGiftCardInput,
  IssueGiftCardOutput,
  SendGiftCardOutput,
} from '../schemas/gift-card-form-schema';
import { CUSTOMER_DEFAULT, fromExpiryInput, toExpiryInput } from '../schemas/gift-card-rules';

export function emptyIssueForm(): IssueGiftCardInput {
  return {
    amount: null,
    customerId: '',
    recipientEmail: '',
    expiresAt: '',
    note: '',
    sendEmail: false,
    message: '',
  };
}

/** The email goes out only with someone to send it to; the toggle is hidden otherwise. */
export function canEmailOnIssue(values: Pick<IssueGiftCardInput, 'customerId' | 'recipientEmail'>) {
  return Boolean(values.customerId || values.recipientEmail.trim());
}

/** Omits what was left blank instead of sending nulls the backend would store as-is. */
export function toGiftCardIssue(
  values: IssueGiftCardOutput,
): PostAdminV1StoresStoreIdGiftCardsBody {
  invariant(values.amount, 'the issue schema requires an amount');
  return {
    amount: values.amount,
    ...(values.customerId ? { customerId: values.customerId } : {}),
    ...(values.recipientEmail ? { recipientEmail: values.recipientEmail } : {}),
    ...(values.expiresAt ? { expiresAt: fromExpiryInput(values.expiresAt) } : {}),
    ...(values.note ? { note: values.note } : {}),
    ...(values.sendEmail && canEmailOnIssue(values)
      ? { sendEmail: true, ...(values.message ? { message: values.message } : {}) }
      : {}),
  };
}

export function emptyAdjustForm(): AdjustGiftCardInput {
  return { direction: 'add', amount: null, note: '' };
}

/** The positive figure typed in the dialog becomes the signed amount the ledger records. */
export function toGiftCardAdjust(
  values: AdjustGiftCardOutput,
): PostAdminV1StoresStoreIdGiftCardsIdAdjustBody {
  invariant(values.amount, 'the adjust schema requires an amount');
  const sign = values.direction === 'remove' ? -1 : 1;
  return {
    amount: { amount: sign * values.amount.amount, currency: values.amount.currency },
    ...(values.note ? { note: values.note } : {}),
  };
}

export function toGiftCardSend(
  values: SendGiftCardOutput,
): PostAdminV1StoresStoreIdGiftCardsIdSendBody {
  return {
    ...(values.to && values.to !== CUSTOMER_DEFAULT ? { to: values.to } : {}),
    ...(values.message ? { message: values.message } : {}),
  };
}

export function fromGiftCard(card: GiftCard): GiftCardEditInput {
  return { expiresAt: toExpiryInput(card.expiresAt), note: card.note ?? '' };
}

/**
 * `expiresAt` goes out only when the day changed: the backend refuses a past expiry, so sending
 * an expired card's own date back would block saving its note.
 */
export function toGiftCardUpdate(
  values: GiftCardEditOutput,
  card: GiftCard,
): PatchAdminV1StoresStoreIdGiftCardsIdBody {
  return {
    ...(values.expiresAt !== toExpiryInput(card.expiresAt)
      ? { expiresAt: fromExpiryInput(values.expiresAt) }
      : {}),
    note: values.note || null,
  };
}
